/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { faLink } from '@fortawesome/free-solid-svg-icons/faLink'
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons/faPlusCircle'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import SearchInput, {
  SearchInputOption,
  SearchInputProps,
  SearchInputRenderOption
} from '../../../../client/js/components/form/fields/SearchInput'
import { getCustomerName } from '../../lib/customer.mjs'
import { fetchLease, findManyLeases } from '../../lib/rest/leaseAPIClient'
import FromToDate from '../FromToDate'
import LeaseStatusBadge from '../lease/LeaseStatusBadge'
import { useQuery } from '@tanstack/react-query'
import { getHalEmbedded } from '@jalik/hal'
import { getLeaseCreateLink, getLeaseLink } from '../../lib/links/leaseLinks'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import { ActionIcon, Box, Group, Text } from '@mantine/core'
import LabelledValue from '../LabelledValue'

function getLeaseOptionLabel (lease: Lease) {
  return lease.Estate
    ? `${lease.reference} - ${lease.Estate.title}`
    : lease.reference
}

function LeaseOption (props: SearchInputRenderOption<Lease>) {
  const { option } = props
  const { item } = option
  const intl = useIntl()
  return (
    <Box w="100%">
      <Group align="start">
        <Text inherit>
          {getLeaseOptionLabel(item)}
        </Text>
        <LeaseStatusBadge
          ml="auto"
          lease={item}
          size="sm"
        />
      </Group>
      <Box fz="sm" c="dimmed">
        {item.Lessor != null
          ? <LabelledValue
            label={intl.formatMessage({
              id: 'owner',
              defaultMessage: 'Propriétaire'
            })}
            value={getCustomerName(item.Lessor)}
          />
          : null}

        {item.Tenant != null
          ? <LabelledValue
            label={intl.formatMessage({
              id: 'tenant',
              defaultMessage: 'Locataire'
            })}
            value={getCustomerName(item.Tenant)}
          />
          : null}

        <FromToDate
          fromDate={item.startDate}
          toDate={item.endDate}
        />
      </Box>
    </Box>
  )
}

export type LeaseInputProps = Omit<SearchInputProps<Lease>, 'data' | 'onSearchChange'> & {
  queryFilter?: Record<string, any>,
}

function LeaseInput (props: LeaseInputProps) {
  const intl = useIntl()
  const {
    disabled,
    id,
    label,
    name = 'leaseId',
    onChange,
    queryFilter,
    required,
    value,
    ...others
  } = props

  const fieldId = id || `${name}-field`

  const [searchParams, setSearchParams] = useState({ limit: 5, search: null })

  const listQuery = useQuery({
    enabled: true,
    queryKey: ['leases', queryFilter, searchParams],
    queryFn: () => findManyLeases({
      ...queryFilter,
      ...searchParams
    }),
    refetchOnWindowFocus: false
  })

  const data = useMemo(() => (
    (listQuery.data != null ? getHalEmbedded(listQuery.data)?.leases : null) || []
  ), [listQuery.data])

  const options: SearchInputOption<Lease>[] = useMemo(() => (
    data.map((el) => ({
      item: el,
      label: getLeaseOptionLabel(el),
      value: String(el.id)
    }))
  ), [data])

  const setSearch = useCallback((search: string) => {
    setSearchParams((s) => ({ ...s, search }))
  }, [])

  const leaseQuery = useQuery({
    enabled: value != null && value !== '',
    queryKey: ['lease', value],
    queryFn: () => fetchLease(parseInt(value, 10)),
    refetchOnWindowFocus: false
  })
  const { data: lease } = leaseQuery

  useEffect(() => {
    if (lease != null && searchParams.search == null) {
      setSearchParams((s) => ({
        ...s, search: getLeaseOptionLabel(lease)
      }))
    }
  }, [lease, searchParams.search])

  return (
    <SearchInput
      id={fieldId}
      label={label || intl.formatMessage({
        id: 'lease',
        defaultMessage: 'Bail de location'
      })}
      {...others}
      data={options}
      disabled={disabled}
      name={name}
      onChange={onChange}
      onSearchChange={setSearch}
      renderOption={LeaseOption}
      required={required}
      searchValue={searchParams.search}
      value={value != null ? String(value) : null}
      rightSection={<>
        {value == null
          ? <ActionIcon
            component={AnchorLink}
            target="_blank"
            title={intl.formatMessage({
              id: 'new',
              defaultMessage: 'Nouveau'
            })}
            to={getLeaseCreateLink()}
          >
            <FontAwesomeIcon
              fixedWidth
              icon={faPlusCircle}
            />
          </ActionIcon>
          : <ActionIcon
            disabled={!value}
            component={AnchorLink}
            target="_blank"
            to={getLeaseLink(Number(value))}
          >
            <FontAwesomeIcon
              fixedWidth
              icon={faLink}
            />
          </ActionIcon>
        }
      </>}
    />
  )
}

export default LeaseInput
